import * as React from "react";
import { Navigate, useNavigate } from "react-router";
import { useMe } from "@/api/me";
import { apiRequestId } from "@/api/error";
import { Handoff } from "@/components/auth/Handoff";
import { useAuthStore } from "@/store/authStore";
import { useViewport } from "@/lib/useViewport";
import { readLastRoute } from "@/native/useLastRoute";
import {
  flattenNav,
  NAV_GROUPS,
  personaHome,
  personaLabel,
} from "@/components/layout/nav";

/**
 * Landing-route dispatcher — ui.md §9.4, redrawn as the hand-off
 * (canvas 01b `auth/Handoff`, `auth/HandoffFailed`, MV2-910).
 *
 * Mounted on `/` inside the AuthGate. Picks the persona-correct home:
 *
 *   SuperAdmin   → /admin/platform/health (desktop) | /overview (mobile)
 *   ClientAdmin  → /overview
 *   OM           → /inbox
 *   SiteOwner    → /my-plants
 *
 * On mobile every persona lands on the last route they were on, else
 * /overview (Site Owner: /my-plants) — admin surfaces are not part of the
 * mobile UX.
 *
 * While /me loads the user sees the branded hand-off; once it lands the
 * screen names the destination and who they are for one beat, then
 * navigates. If /me fails it is retried three times with backoff and then
 * shows Retry + Sign out — never a spinner forever.
 */

const AUTO_RETRIES = 3;
/** The beat the resolved hand-off holds before navigating. */
const HOLD_MS = 450;

/** Route → the noun the hand-off names ("Taking you to your Inbox"). */
export function destinationLabel(path: string): string {
  const items = flattenNav(NAV_GROUPS);
  const exact = items.find((i) => i.to === path);
  if (exact) return exact.label;
  if (path.startsWith("/admin/platform")) return "Platform health";
  const prefix = items
    .filter((i) => i.to !== "/" && path.startsWith(i.to))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return prefix ? prefix.label : "home";
}

export function DashboardRouter(): React.ReactElement {
  const status = useAuthStore((s) => s.status);
  const sessionEmail = useAuthStore((s) => s.email);
  const signOutAll = useAuthStore((s) => s.signOutAll);
  const navigate = useNavigate();
  const me = useMe({ enabled: status === "authenticated" });
  const { isMobile } = useViewport();

  const [attempts, setAttempts] = React.useState(0);
  const [signingOut, setSigningOut] = React.useState(false);
  const [held, setHeld] = React.useState(false);

  // Automatic retries with backoff (0.5 s, 1 s, 2 s) before we give up and
  // hand the user the button.
  React.useEffect(() => {
    if (!me.isError || attempts >= AUTO_RETRIES) return;
    const t = window.setTimeout(
      () => {
        setAttempts((n) => n + 1);
        void me.refetch();
      },
      500 * 2 ** attempts,
    );
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.isError, attempts]);

  // Hold the resolved hand-off for one beat so the destination is read.
  React.useEffect(() => {
    if (!me.data) return;
    const t = window.setTimeout(() => setHeld(true), HOLD_MS);
    return () => window.clearTimeout(t);
  }, [me.data]);

  if (me.isError && attempts >= AUTO_RETRIES && !me.isFetching) {
    return (
      <Handoff
        state="failed"
        retries={attempts}
        requestId={apiRequestId(me.error)}
        busy={signingOut}
        onRetry={() => {
          setAttempts(0);
          void me.refetch();
        }}
        onSignOut={() => {
          setSigningOut(true);
          void signOutAll().finally(() =>
            navigate("/login", { replace: true }),
          );
        }}
      />
    );
  }

  if (!me.data) {
    return <Handoff state="loading" identity={sessionEmail} />;
  }

  const target = isMobile
    ? // Restore the last page the user was on before Android killed the
      // process (Activity recreation / process death).
      (readLastRoute() ??
      personaHome(me.data.primary_persona, { isMobile: true }))
    : personaHome(me.data.primary_persona);

  if (held) return <Navigate to={target} replace />;

  return (
    <Handoff
      state="ready"
      destination={destinationLabel(target)}
      identity={[
        me.data.full_name,
        personaLabel(me.data.primary_persona),
        me.data.client?.name,
      ]
        .filter(Boolean)
        .join(" · ")}
    />
  );
}
